import { h } from 'preact';
import { useEffect, useState, useRef } from 'preact/hooks';
import Player from '@vimeo/player';
import { trackPDPGalleryClick } from 'utils/tracking';
import { Pause, Play, Unmute, Mute } from '../utils/icon/video-icons';

const Video = ({
  video,
  previousVideo,
  playCurrentVideo,
  autoPlay,
  controls,
  classes = '',
  shouldPreload = false,
  isActive = false,
}) => {
  const [player, setPlayer] = useState(null);
  const [isPlaying, setIsPlaying] = useState(autoPlay);
  const [isMuted, setIsMuted] = useState(true);
  const [showPoster, setShowPoster] = useState(true);
  const isActiveRef = useRef(isActive);

  useEffect(() => {
    isActiveRef.current = isActive;
    if (!isActive) setShowPoster(true);
  }, [isActive]);

  const getVideoId = (id) => id.split('Video/')[1];
  const getEmbeddedUrl = (url) => url.split('/video/')[1];

  const unmuteVideo = () => {
    if (!player) return;
    if (video.host && video.host.toLowerCase() === 'vimeo') {
      player.setVolume(1);
    } else player.muted = false;
    setIsMuted(false);
  };

  const muteVideo = () => {
    if (!player) return;
    if (video.host && video.host.toLowerCase() === 'vimeo') {
      player.setVolume(0);
    } else player.muted = true;
    setIsMuted(true);
  };

  const handleMute = () => (isMuted ? unmuteVideo() : muteVideo());

  const pauseVideo = () => {
    if (!player) return;
    player.pause();
    setIsPlaying(false);
  };

  const playVideo = () => {
    if (!player) return;
    player.play();
    setIsPlaying(true);
    trackPDPGalleryClick('video play');
  };

  const handlePlay = () => (isPlaying ? pauseVideo() : playVideo());

  const initVideo = () => {
    if (video.host && video.host.toLowerCase() === 'vimeo') {
      const vimeo = new Player(`video-${getVideoId(video.id)}`, {
        id: getEmbeddedUrl(video.embeddedUrl),
        controls: false,
        autoplay: autoPlay,
        muted: true,
        responsive: true,
        loop: true,
        background: true,
      });
      // Keep poster until we know first frame is rendering on ACTIVE slide
      vimeo.on('timeupdate', (data) => {
        if (data && typeof data.seconds === 'number' && data.seconds > 0 && isActiveRef.current) {
          setShowPoster(false);
        }
      });
      vimeo.on('loaded', () => {
        if (!autoPlay) setShowPoster(true);
      });
      setPlayer(vimeo);
    } else {
      setPlayer(document.querySelector(`#video-${getVideoId(video.id)} video`));
    }
  };

  useEffect(() => {
    if (!video) return;
    initVideo();
  }, [video]);

  useEffect(() => {
    if (!previousVideo) return;
    if (previousVideo === `video-${getVideoId(video.id)}`) {
      pauseVideo();
      muteVideo();
    }
  }, [previousVideo]);

  useEffect(() => {
    if (!playCurrentVideo) return;
    if (playCurrentVideo === `video-${getVideoId(video.id)}`) {
      playVideo();
    }
  }, [playCurrentVideo]);

  // Ensure overlay stays until the first frame for HTML5 video as well
  useEffect(() => {
    if (!player) return () => {};
    let cleanup = () => {};
    if (!(video.host && video.host.toLowerCase() === 'vimeo')) {
      const html5 = player;
      const onTimeUpdate = () => {
        if (html5.currentTime && html5.currentTime > 0 && isActiveRef.current) {
          setShowPoster(false);
        }
      };
      const onPlaying = () => {
        if (html5.currentTime && html5.currentTime > 0 && isActiveRef.current) {
          setShowPoster(false);
        }
      };
      html5.addEventListener('timeupdate', onTimeUpdate);
      html5.addEventListener('playing', onPlaying);
      html5.addEventListener('loadeddata', onTimeUpdate);
      cleanup = () => {
        html5.removeEventListener('timeupdate', onTimeUpdate);
        html5.removeEventListener('playing', onPlaying);
        html5.removeEventListener('loadeddata', onTimeUpdate);
      };
    }
    return cleanup;
  }, [player, video]);

  useEffect(() => {
    if (!shouldPreload || !player) return;
    if (video.host && video.host.toLowerCase() === 'vimeo') {
      // Touch the player to warm caches without visible playback
      try { player.getDuration(); } catch (e) { /* no-op */ }
      try { player.getVideoTitle(); } catch (e) { /* no-op */ }
      player.ready().then(() => {
        player.play().then(() => {
          setTimeout(() => {
            player.pause();
          }, 100);
        }).catch(() => { /* ignore autoplay prevention during warmup */ });
      });
    } else {
      const el = document.querySelector(`#video-${getVideoId(video.id)} video`);
      if (el) {
        try {
          el.preload = 'auto';
          el.load();
        } catch (e) { /* no-op */ }
      }
    }
  }, [shouldPreload, player]);

  return (
    <div className="relative z-1 w-full h-full">
      {showPoster && video?.previewImage?.src && (
        <img
          src={video.previewImage.src}
          alt={video.previewImage.alt || 'Video preview'}
          className="absolute inset-0 z-10 w-full h-full object-cover transition-opacity duration-200 pointer-events-none"
          style={{ opacity: showPoster ? 1 : 0 }}
        />
      )}
      <div
        data-video-component
        id={`video-${getVideoId(video.id)}`}
        className="absolute inset-0 w-full h-full overflow-hidden"
      >
        {video.mediaContentType.toLowerCase() === 'video' ? (
          <video
            className={classes}
            autoPlay={autoPlay}
            muted={isMuted}
            playsInline
            preload="metadata"
            poster={video?.previewImage?.src}
            loop
          >
            {video.sources.map((source) => (
              <source
                key={source.id}
                src={source.url}
                type={source.mime_type}
                height={source.height}
                width={source.width}
              />
            ))}
          </video>
        ) : null}
      </div>

      {controls && (
        <div className="absolute bottom-0 right-0 z-10 flex">
          <button
            className="text-white"
            type="button"
            aria-label={`${isPlaying ? 'play' : 'pause'}`}
            onClick={handlePlay}
          >
            <span className={`${isPlaying && 'hidden'}`}>
              <Play />
            </span>
            <span className={`${!isPlaying && 'hidden'}`}>
              <Pause />
            </span>
          </button>

          <button
            className="text-white"
            type="button"
            aria-label={`${isMuted ? 'sound-off' : 'sound-on'}`}
            onClick={handleMute}
          >
            <span className={`${isMuted && 'hidden'}`}>
              <Unmute />
            </span>
            <span className={`${!isMuted && 'hidden'}`}>
              <Mute />
            </span>
          </button>
        </div>
      )}
    </div>
  );
};

export default Video;
